import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Card, Notice } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';

interface Slot {
  weekday: number;
  start: string;
  end: string;
}
const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const ORDER = [1, 2, 3, 4, 5, 6, 0];

export function AvailabilityPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['availability'], queryFn: () => api<Slot[]>('/availability') });
  const [slots, setSlots] = useState<Slot[]>([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => q.data && setSlots(q.data), [q.data]);

  const update = (i: number, patch: Partial<Slot>) => setSlots((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const save = async () => {
    setSaving(true);
    try {
      await api('/availability', { method: 'PUT', body: { slots } });
      await qc.invalidateQueries({ queryKey: ['availability'] });
      toast('Horario guardado', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title="Disponibilidad" back="/driver/more">
      <div className="flex flex-col gap-4">
        <Notice>Las solicitudes fuera de este horario se marcan como fuera de disponibilidad; tú decides si las cotizas.</Notice>
        {ORDER.map((day) => {
          const daySlots = slots.map((s, i) => [s, i] as const).filter(([s]) => s.weekday === day);
          return (
            <Card key={day} className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{DAYS[day]}</h3>
                <button
                  className="tap inline-flex items-center gap-1 text-sm text-brand-red font-medium"
                  onClick={() => setSlots((s) => [...s, { weekday: day, start: '08:00', end: '18:00' }])}
                  aria-label={`Agregar horario el ${DAYS[day]}`}
                >
                  <Plus className="size-4" aria-hidden /> Agregar
                </button>
              </div>
              {daySlots.length === 0 && <p className="text-sm text-[var(--text-muted)] mt-1">No disponible</p>}
              {daySlots.map(([s, i]) => (
                <div key={i} className="mt-3 flex items-center gap-2">
                  <input type="time" aria-label="Desde" value={s.start} onChange={(e) => update(i, { start: e.target.value })} className="h-11 flex-1 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3" />
                  <span className="text-[var(--text-muted)]">a</span>
                  <input type="time" aria-label="Hasta" value={s.end} onChange={(e) => update(i, { end: e.target.value })} className="h-11 flex-1 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3" />
                  <button className="tap flex items-center justify-center text-[var(--text-muted)] hover:text-brand-red" aria-label="Quitar horario" onClick={() => setSlots((x) => x.filter((_, j) => j !== i))}>
                    <Trash2 className="size-5" />
                  </button>
                </div>
              ))}
            </Card>
          );
        })}
        <Button size="lg" onClick={save} loading={saving}>Guardar horario</Button>
      </div>
    </Page>
  );
}
